/**
 * Simple HTTP client to replace AxiosHttpClient from messaging-common-js
 */
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
    HttpClientOptions,
    HttpClientResponse,
    IHttpClient,
    HttpClientError,
} from '../../types/messaging-types';

export class SimpleHttpClient implements IHttpClient {
    private readonly client: AxiosInstance;

    constructor(options: HttpClientOptions) {
        const { baseURL, timeout, headers, xsrfCookieName, xsrfHeaderName, ...restOptions } =
            options;
        this.client = axios.create({
            baseURL,
            timeout: timeout || 10000,
            headers: headers || {},
            withCredentials: true, // Enable sending cookies with cross-origin requests
            // Axios built-in CSRF protection
            ...(xsrfCookieName && { xsrfCookieName }),
            ...(xsrfHeaderName && { xsrfHeaderName }),
            ...restOptions,
        });
    }

    private convertResponse(axiosResponse: AxiosResponse): HttpClientResponse {
        return {
            status: axiosResponse.status,
            data: axiosResponse.data,
            headers: axiosResponse.headers as Record<string, string>,
        };
    }

    async get(url: string, config?: any): Promise<HttpClientResponse> {
        try {
            const response = await this.client.get(url, config);
            return this.convertResponse(response);
        } catch (error: any) {
            throw new HttpClientError(
                error.message,
                error.response ? this.convertResponse(error.response) : undefined,
                error.request,
                error.config
            );
        }
    }

    async post(url: string, data?: any, config?: any): Promise<HttpClientResponse> {
        try {
            const response = await this.client.post(url, data, config);
            return this.convertResponse(response);
        } catch (error: any) {
            throw new HttpClientError(
                error.message,
                error.response ? this.convertResponse(error.response) : undefined,
                error.request,
                error.config
            );
        }
    }

    async put(url: string, data?: any, config?: any): Promise<HttpClientResponse> {
        try {
            const response = await this.client.put(url, data, config);
            return this.convertResponse(response);
        } catch (error: any) {
            throw new HttpClientError(
                error.message,
                error.response ? this.convertResponse(error.response) : undefined,
                error.request,
                error.config
            );
        }
    }

    async delete(url: string, config?: any): Promise<HttpClientResponse> {
        try {
            const response = await this.client.delete(url, config);
            return this.convertResponse(response);
        } catch (error: any) {
            throw new HttpClientError(
                error.message,
                error.response ? this.convertResponse(error.response) : undefined,
                error.request,
                error.config
            );
        }
    }
}
